/**
 * CONTRACT (Wave 0 · T0.1) — FROZEN.
 * The interim, BFF-hosted onboarding definition (Decision D4 / Gap G2).
 *
 * `OnboardingDefinitionV1` drives BOTH the wizard step set AND the store-cloning
 * payload. It is fetched via `GET /v1/onboarding/definition?version=latest`
 * (BFF-mocked now). Designed so the source can later move to tenancy / a shared
 * schema package with no frontend change.
 *
 * Downstream tasks IMPORT from this file and never edit it. A change here is a
 * serialized contract-amendment task, not an inline edit.
 */
import { z } from 'zod';

/** Canonical step ids (align with `WizardStep` in wizard-state.ts). */
export const STEP_IDS = [
  'account',
  'objective',
  'bottleneck',
  'channels',
  'situation',
  'preparing',
] as const;
export type StepId = (typeof STEP_IDS)[number];

/** How a step is rendered / validated. */
export const StepKindSchema = z.enum(['account', 'single', 'multi', 'preparing']);
export type StepKind = z.infer<typeof StepKindSchema>;

export const OptionSchema = z.object({
  id: z.string(),
  label: z.string(),
});
export type Option = z.infer<typeof OptionSchema>;

export const StepDefSchema = z.object({
  id: z.enum(STEP_IDS),
  kind: StepKindSchema,
  title: z.string(),
  description: z.string().optional(),
  /** Present for `single` / `multi` steps. */
  options: z.array(OptionSchema).optional(),
});
export type StepDef = z.infer<typeof StepDefSchema>;

/**
 * Reference to the store-cloning template the provisioning orchestrator passes
 * to the cloner. Kept opaque so the real cloner contract (Gap G1) can fill it.
 */
export const CloningTemplateRefSchema = z.object({
  templateId: z.string(),
  source: z.string(),
  version: z.string().optional(),
});
export type CloningTemplateRef = z.infer<typeof CloningTemplateRefSchema>;

export const OnboardingDefinitionV1Schema = z.object({
  version: z.string(),
  steps: z.array(StepDefSchema),
  cloning: CloningTemplateRefSchema,
});
export type OnboardingDefinitionV1 = z.infer<typeof OnboardingDefinitionV1Schema>;

/* ------------------------------------------------------------------------- *
 * Canonical option id <-> label maps (source of truth: plan §5).
 * Step tasks build OptionCards from these; the BFF fixture (T2.6) reuses them.
 * ------------------------------------------------------------------------- */

export const OBJECTIVE_OPTIONS: Option[] = [
  { id: 'cvr', label: 'Increase CVR' },
  { id: 'aov', label: 'Increase AOV' },
  { id: 'roas', label: 'Improve blended ROAS' },
  { id: 'cac', label: 'Reduce CAC' },
  { id: 'checkout', label: 'Improve checkout performance' },
];

export const BOTTLENECK_OPTIONS: Option[] = [
  { id: 'ads', label: 'Ads performance' },
  { id: 'landing', label: 'Landing pages' },
  { id: 'pdp', label: 'PDP conversion' },
  { id: 'cart', label: 'Cart abandonment' },
  { id: 'checkout', label: 'Checkout friction' },
];

export const CHANNEL_OPTIONS: Option[] = [
  { id: 'meta', label: 'Meta Ads' },
  { id: 'google', label: 'Google Ads' },
  { id: 'tiktok', label: 'TikTok Ads' },
  { id: 'pinterest', label: 'Pinterest Ads' },
  { id: 'programmatic', label: 'Programmatic / Display' },
];

export const SITUATION_OPTIONS: Option[] = [
  { id: 'scaling', label: 'Scaling aggressively' },
  { id: 'plateau', label: 'Profitable but plateauing' },
  { id: 'pmf', label: 'Testing product-market fit' },
  { id: 'high-traffic', label: 'High traffic, low conversion' },
  { id: 'high-conv', label: 'Strong conversion, weak traffic' },
];

/** Helper: map an option id to its label within a given option set. */
export function labelForOption(options: Option[], id: string): string | undefined {
  return options.find((o) => o.id === id)?.label;
}
